/* eslint-disable no-nested-ternary */
import { I_Hotspot, I_Post } from '@containers/Home/types';
import dynamic from 'next/dynamic';
import React, {
  forwardRef,
  Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { HotspotMediaWrapper } from './styled';

const DialogPostWithoutUseRedux = dynamic(
  () => import('@components/DialogPost/DialogPostWithoutUseRedux'),
);

interface Props {
  hotspot: I_Hotspot;
  listPost?: I_Post[];
  isSoundOn?: boolean;
  onClickHotspot: ({ hotspot }) => void;
}

export type TypeRefHotspotMedia = {
  onToggleMute: (isMute: boolean) => void;
};

const HotspotMedia = forwardRef(
  (
    { hotspot, listPost, isSoundOn, onClickHotspot }: Props,
    ref: Ref<TypeRefHotspotMedia>,
  ) => {
    const videoRef = useRef<HTMLVideoElement | null>(null!);
    const [isVideoPlaying, setIsVideoPlaying] = useState(false);
    const [visibleDialogPost, setVisibleDialogPost] = useState(false);

    const postId = useMemo(
      () =>
        hotspot?.config?.media?.type === 'media-image'
          ? hotspot?.config?.media?.post_id
          : '',
      [hotspot],
    );
    const type = useMemo(
      () => hotspot?.config?.media?.type || 'media-video',
      [hotspot],
    );
    const isVideoLoop = useMemo(
      () =>
        hotspot?.config?.media?.type === 'media-video'
          ? hotspot?.config?.media?.loop || false
          : false,
      [hotspot],
    );
    const isVideoAutoplay = useMemo(
      () =>
        hotspot?.config?.media?.type === 'media-video'
          ? hotspot?.config?.media?.autoPlay || false
          : false,
      [hotspot],
    );
    const videoVolume = useMemo(
      () =>
        hotspot?.config?.media?.type === 'media-video'
          ? hotspot?.config?.media?.volume || 0
          : 0,
      [hotspot],
    );
    const actionClick = useMemo(
      () => hotspot?.config?.media?.action,
      [hotspot],
    );
    const width = useMemo(
      () => hotspot?.config?.media?.width || '250px',
      [hotspot],
    );
    const height = useMemo(
      () => hotspot?.config?.media?.height || '200px',
      [hotspot],
    );
    const transform = useMemo(
      () => hotspot?.config?.media?.transform || '',
      [hotspot],
    );
    const rotate = useMemo(
      () => hotspot?.config?.media?.rotate || 0,
      [hotspot],
    );
    const backgroundColor = useMemo(
      () => hotspot?.config?.media?.backgroundColor || '#00000000',
      [hotspot],
    );
    const opacity = useMemo(
      () => hotspot?.config?.media?.opacity || 1,
      [hotspot],
    );
    const displayType = useMemo(
      () => hotspot?.config?.media?.displayType || 'none',
      [hotspot],
    );
    const content_post = useMemo(
      () => hotspot?.config?.media?.content || '',
      [hotspot],
    );

    useEffect(() => {
      if (type === 'media-video' && videoRef.current)
        if (isVideoAutoplay)
          try {
            videoRef.current.muted = true;
            videoRef.current.autoplay = true;
            setIsVideoPlaying(true);
          } catch (err) {
            console.log(err);
          }
        else videoRef.current.pause();
    }, [isVideoAutoplay, type]);

    useEffect(() => {
      if (type === 'media-video' && videoRef.current) {
        videoRef.current.volume = isSoundOn ? videoVolume / 100 : 0;
        videoRef.current.muted = !isSoundOn || videoVolume === 0;
      }
    }, [videoVolume, isSoundOn, type]);

    const onOpenDialogPost = useCallback(() => setVisibleDialogPost(true), []);
    const onCloseDialogPost = useCallback(
      () => setVisibleDialogPost(false),
      [],
    );

    const onClick = useCallback(() => {
      switch (type) {
        case 'media-video':
          switch (actionClick) {
            case 'play/pause':
              setIsVideoPlaying(videoRef.current?.paused || false);
              if (videoRef.current?.paused) {
                videoRef.current.play();
              } else videoRef.current?.pause();
              break;
            default:
              onClickHotspot({ hotspot });
              break;
          }
          break;
        case 'media-image':
          switch (actionClick) {
            case 'open-post':
              onOpenDialogPost();
              break;
            default:
              onClickHotspot({ hotspot });
              break;
          }
          break;
        default:
          break;
      }
    }, [actionClick, hotspot, onClickHotspot, onOpenDialogPost, type]);

    useImperativeHandle(
      ref,
      () => ({
        onToggleMute: isMute => {
          if (videoRef.current) {
            videoRef.current.muted = isMute;
            videoRef.current.volume = !isMute ? videoVolume / 100 : 0;
          }
        },
      }),
      [videoVolume],
    );

    return (
      <>
        <HotspotMediaWrapper
          width={width}
          height={height}
          opacity={opacity}
          rotate={rotate}
          transform={transform}
          displayType={displayType}
          backgroundColor={backgroundColor}
        >
          {type === 'media-video' ? (
            <>
              {actionClick && (
                <i
                  className={`fas fa-${
                    actionClick === 'zoom'
                      ? 'search-plus'
                      : actionClick === 'play/pause'
                      ? isVideoPlaying
                        ? 'pause'
                        : 'play'
                      : ''
                  }`}
                  onClick={onClick}
                  onTouchStart={onClick}
                />
              )}
              <video
                ref={videoRef}
                loop={isVideoLoop}
                autoPlay={true}
                className="target"
                playsInline
                muted={true}
                preload="metadata"
              >
                {hotspot?.config?.media?.type === 'media-video' &&
                  hotspot?.config?.media?.mediaIOS?.src && (
                    <source
                      src={
                        hotspot?.config?.media?.type === 'media-video'
                          ? hotspot?.config?.media?.mediaIOS?.src + '#t=0.001'
                          : ''
                      }
                      type='video/mp4; codecs="hvc1"'
                    />
                  )}
                <source
                  src={hotspot?.config?.media?.media?.src || '' + '#t=0.001'}
                  type={'video/mp4'}
                />
              </video>
            </>
          ) : type === 'media-image' ? (
            <>
              {actionClick && (
                <i
                  aria-hidden
                  className={`fas fa-${
                    actionClick === 'zoom'
                      ? 'search-plus'
                      : actionClick === 'open-post'
                      ? 'info-circle'
                      : ''
                  }`}
                  onClick={onClick}
                  onTouchStart={onClick}
                />
              )}
              <img
                src={hotspot?.config?.media?.media?.src}
                className="target"
                alt=""
              />
            </>
          ) : type === 'media-text' ? (
            <div
              className="target"
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: content_post || '' }}
              onClick={onClick}
              onTouchStart={onClick}
            />
          ) : (
            <div />
          )}
        </HotspotMediaWrapper>

        {visibleDialogPost && postId && (
          <DialogPostWithoutUseRedux
            idPost={postId}
            listPost={listPost || []}
            visible={visibleDialogPost}
            onCancel={onCloseDialogPost}
          />
        )}
      </>
    );
  },
);

export default HotspotMedia;
